import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Search, Plus, Filter, Pencil, Trash2, X, ArrowDownLeft, ArrowUpRight, Paperclip, Upload, FileText, ExternalLink, Loader2 } from "lucide-react";

const fileUrl = (u) => `${process.env.REACT_APP_BACKEND_URL}${u}`;

const empty = { date: new Date().toISOString().slice(0, 10), description: "", amount: "", type: "debit",
  category: "Miscellaneous", subcategory: "", account_id: "", payment_method: "", reference: "", notes: "", tags: [], attachments: [] };

export default function Transactions() {
  const { companyId, currency, categories } = useApp();
  const { hasPermission } = useAuth();
  const [txns, setTxns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", type: "", category: "", account_id: "" });
  const [sort, setSort] = useState({ sort: "date", order: "desc" });
  const [modal, setModal] = useState(null);
  const [viewer, setViewer] = useState(null);

  const canManage = hasPermission("manage_transactions");

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { ...sort };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get(`/companies/${companyId}/transactions`, { params });
      setTxns(data);
    } catch {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [companyId, filters, sort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (companyId) api.get(`/companies/${companyId}/accounts`).then(({ data }) => setAccounts(data));
  }, [companyId]);

  const save = async () => {
    const body = { ...modal, amount: parseFloat(modal.amount) || 0,
      tags: typeof modal.tags === "string" ? modal.tags.split(",").map((t) => t.trim()).filter(Boolean) : modal.tags };
    try {
      if (modal.id) await api.put(`/companies/${companyId}/transactions/${modal.id}`, body);
      else await api.post(`/companies/${companyId}/transactions`, body);
      toast.success("Transaction saved");
      setModal(null);
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    await api.delete(`/companies/${companyId}/transactions/${id}`);
    toast.success("Deleted");
    load();
  };

  const acctName = (id) => accounts.find((a) => a.id === id)?.name || "\u2014";

  return (
    <div className="space-y-5 animate-fade-up" data-testid="transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Transactions</h1>
          <p className="text-slate-500 text-sm mt-0.5">{txns.length} records</p>
        </div>
        {canManage && (
          <button data-testid="add-txn-btn" onClick={() => setModal({ ...empty })}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 active:scale-95 transition-all">
            <Plus size={18} /> Add Transaction
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="txn-search" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search description, reference, notes..."
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none" />
        </div>
        <select data-testid="filter-type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white outline-none">
          <option value="">All types</option><option value="credit">Money In</option><option value="debit">Money Out</option>
        </select>
        <select data-testid="filter-category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white outline-none max-w-[180px]">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="sort-select" value={`${sort.sort}:${sort.order}`}
          onChange={(e) => { const [s, o] = e.target.value.split(":"); setSort({ sort: s, order: o }); }}
          className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white outline-none">
          <option value="date:desc">Newest first</option><option value="date:asc">Oldest first</option>
          <option value="amount:desc">Amount high-low</option><option value="amount:asc">Amount low-high</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-5 py-3">Date</th>
                <th className="text-left font-semibold px-5 py-3">Description</th>
                <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Category</th>
                <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">Account</th>
                <th className="text-right font-semibold px-5 py-3">Amount</th>
                {canManage && <th className="px-5 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-slate-400">Loading...</td></tr>
              ) : txns.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-slate-400">No transactions found</td></tr>
              ) : txns.map((t) => (
                <tr key={t.id} data-testid={`txn-row-${t.id}`} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{formatDate(t.date)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${t.type === "credit" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                        {t.type === "credit" ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                      </span>
                      <span className="font-medium text-slate-800">{t.description}</span>
                      {t.attachments?.length > 0 && (
                        <button data-testid={`view-attach-${t.id}`} onClick={(e) => { e.stopPropagation(); setViewer(t); }}
                          className="inline-flex items-center gap-0.5 text-xs text-indigo-600 font-semibold hover:underline">
                          <Paperclip size={13} />{t.attachments.length}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell">
                    <span className="text-xs font-semibold px-2 py-1 rounded-lg bg-slate-100 text-slate-600">{t.category}</span>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell text-slate-500">{acctName(t.account_id)}</td>
                  <td className={`px-5 py-3 text-right font-bold whitespace-nowrap ${t.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                    {t.type === "credit" ? "+" : "-"}{formatMoney(t.amount, currency)}
                  </td>
                  {canManage && (
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button data-testid={`edit-txn-${t.id}`} onClick={() => setModal({ ...t, tags: (t.tags || []).join(", ") })} className="p-1.5 text-slate-400 hover:text-indigo-600"><Pencil size={15} /></button>
                        <button data-testid={`delete-txn-${t.id}`} onClick={() => del(t.id)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <TxnModal modal={modal} setModal={setModal} save={save} categories={categories} accounts={accounts} />
      )}

      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setViewer(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="attachment-viewer">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 className="font-heading font-bold text-lg truncate">Attachments · {viewer.description}</h3>
              <button onClick={() => setViewer(null)}><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(viewer.attachments || []).map((a, i) => (
                <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer" data-testid={`viewer-file-${i}`}
                  className="block rounded-xl border border-slate-200 overflow-hidden hover:border-indigo-400 transition-colors">
                  {a.content_type?.startsWith("image/") ? (
                    <img src={fileUrl(a.url)} alt={a.filename} className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 bg-slate-50 grid place-items-center"><FileText size={32} className="text-slate-300" /></div>
                  )}
                  <p className="text-xs text-slate-600 p-2 truncate">{a.filename}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TxnModal({ modal, setModal, save, categories, accounts }) {
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setModal({ ...modal, [k]: v });
  const onUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setModal((m) => ({ ...m, attachments: [...(m.attachments || []), { id: data.id, url: data.url, filename: data.filename, content_type: data.content_type }] }));
      toast.success("Attached");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const removeAttachment = (i) => setModal((m) => ({ ...m, attachments: (m.attachments || []).filter((_, idx) => idx !== i) }));
  const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setModal(null)}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="txn-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-heading font-bold text-lg">{modal.id ? "Edit" : "Add"} Transaction</h3>
          <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Date"><input type="date" data-testid="modal-date" value={modal.date} onChange={(e) => set("date", e.target.value)} className={inp} /></Fld>
            <Fld label="Type">
              <select data-testid="modal-type" value={modal.type} onChange={(e) => set("type", e.target.value)} className={inp}>
                <option value="debit">Money Out (Debit)</option><option value="credit">Money In (Credit)</option>
              </select>
            </Fld>
          </div>
          <Fld label="Description"><input data-testid="modal-description" value={modal.description} onChange={(e) => set("description", e.target.value)} className={inp} placeholder="e.g. Client invoice" /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Amount"><input type="number" data-testid="modal-amount" value={modal.amount} onChange={(e) => set("amount", e.target.value)} className={inp} placeholder="0.00" /></Fld>
            <Fld label="Category">
              <select data-testid="modal-category" value={modal.category} onChange={(e) => set("category", e.target.value)} className={inp}>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Fld>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Account">
              <select data-testid="modal-account" value={modal.account_id || ""} onChange={(e) => set("account_id", e.target.value)} className={inp}>
                <option value="">None</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Fld>
            <Fld label="Payment Method"><input data-testid="modal-method" value={modal.payment_method || ""} onChange={(e) => set("payment_method", e.target.value)} className={inp} placeholder="UPI, NEFT..." /></Fld>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Reference"><input value={modal.reference || ""} onChange={(e) => set("reference", e.target.value)} className={inp} /></Fld>
            <Fld label="Tags (comma sep)"><input value={modal.tags} onChange={(e) => set("tags", e.target.value)} className={inp} /></Fld>
          </div>
          <Fld label="Notes"><textarea value={modal.notes || ""} onChange={(e) => set("notes", e.target.value)} className={inp} rows={2} /></Fld>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Attachments (receipts / invoices)</label>
            <div className="space-y-2">
              {(modal.attachments || []).map((a, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-xl border border-slate-200" data-testid={`attachment-item-${i}`}>
                  {a.content_type?.startsWith("image/") ? (
                    <img src={fileUrl(a.url)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-slate-100 grid place-items-center"><FileText size={18} className="text-slate-400" /></div>
                  )}
                  <span className="flex-1 text-sm text-slate-700 truncate">{a.filename}</span>
                  <a href={fileUrl(a.url)} target="_blank" rel="noreferrer" className="p-1 text-slate-400 hover:text-indigo-600"><ExternalLink size={15} /></a>
                  <button type="button" onClick={() => removeAttachment(i)} className="p-1 text-slate-400 hover:text-rose-600"><X size={15} /></button>
                </div>
              ))}
              <label className="flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 border-dashed border-slate-200 cursor-pointer hover:border-indigo-400 text-sm text-slate-500 font-medium">
                <input type="file" className="hidden" data-testid="attachment-input" onChange={onUpload} accept="image/*,application/pdf" />
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploading ? "Uploading..." : "Attach receipt / invoice"}
              </label>
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-slate-100 flex gap-3 sticky bottom-0 bg-white">
          <button onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 font-semibold text-slate-600">Cancel</button>
          <button data-testid="modal-save-btn" onClick={save} className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">Save</button>
        </div>
      </div>
    </div>
  );
}

const Fld = ({ label, children }) => (
  <div><label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>{children}</div>
);
